import { DropdownMenu } from "@kobalte/core/dropdown-menu"
import { For, Show, createSignal, onCleanup, type JSXElement } from "solid-js"
import { MoreHorizontal } from "lucide-solid"
import { suppressCompatibilityClick } from "../lib/compatibility-click"

export interface ActionOverflowMenuItem {
  key: string
  label: string
  description?: string
  icon?: JSXElement
  disabled?: boolean
  checked?: boolean
  onSelect: () => void | Promise<void>
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

interface ActionOverflowMenuProps {
  items: ActionOverflowMenuItem[]
  label: string
  triggerClass?: string
  minItems?: number
  onOpenChange?: (open: boolean) => void
}

export default function ActionOverflowMenu(props: ActionOverflowMenuProps) {
  let selectedAction: ActionOverflowMenuItem["onSelect"] | undefined
  let pointerType: string | undefined
  const [hoveredItem, setHoveredItem] = createSignal<ActionOverflowMenuItem | null>(null)
  const enabledItems = () => props.items.filter((item) => !item.disabled)
  const hasItems = () => props.items.length >= (props.minItems ?? 1)
  const clearHoveredItem = () => {
    const item = hoveredItem()
    if (!item) return
    item.onMouseLeave?.()
    setHoveredItem(null)
  }

  onCleanup(clearHoveredItem)

  return (
    <Show when={hasItems()}>
      <DropdownMenu placement="bottom-end" gutter={4} onOpenChange={(open) => {
        if (!open) clearHoveredItem()
        props.onOpenChange?.(open)
      }}>
        <DropdownMenu.Trigger
          class={`action-overflow-trigger ${props.triggerClass ?? ""}`.trim()}
          aria-label={props.label}
          title={props.label}
          disabled={enabledItems().length === 0}
        >
          <MoreHorizontal class="w-3.5 h-3.5" aria-hidden="true" />
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content class="action-overflow-content" onCloseAutoFocus={() => {
            // Kobalte restores the trigger after this callback. Launch selected
            // actions afterwards so a newly opened window keeps its autofocus.
            const action = selectedAction
            selectedAction = undefined
            if (action) queueMicrotask(() => { void action() })
          }}>
            <For each={props.items}>
              {(item) => (
                <DropdownMenu.Item
                  class="action-overflow-item"
                  role={typeof item.checked === "boolean" ? "menuitemcheckbox" : undefined}
                  aria-checked={typeof item.checked === "boolean" ? item.checked : undefined}
                  disabled={item.disabled}
                  aria-description={item.description}
                  title={item.description}
                  onPointerEnter={() => {
                    if (item.disabled) return
                    const previous = hoveredItem()
                    if (previous !== item) previous?.onMouseLeave?.()
                    setHoveredItem(item)
                    item.onMouseEnter?.()
                  }}
                  onPointerLeave={() => {
                    if (item.disabled) return
                    if (hoveredItem() === item) setHoveredItem(null)
                    item.onMouseLeave?.()
                  }}
                  // Kobalte selects on pointerup; record it for the selection below.
                  onPointerUp={(event) => { pointerType = event.pointerType }}
                  onKeyDown={() => { pointerType = undefined }}
                  onSelect={() => {
                    clearHoveredItem()
                    selectedAction = item.onSelect
                    if (pointerType && pointerType !== "mouse") suppressCompatibilityClick()
                    pointerType = undefined
                  }}
                >
                  <Show when={item.icon} fallback={<span class="action-overflow-item-icon" aria-hidden="true" />}>
                    {(icon) => <span class="action-overflow-item-icon" aria-hidden="true">{icon()}</span>}
                  </Show>
                  <span class="action-overflow-item-label">{item.label}</span>
                </DropdownMenu.Item>
              )}
            </For>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu>
    </Show>
  )
}
